import { useCallback, useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import './LiquidNav.css';

/**
 * LiquidNav — a frosted-glass command dock with a single morphing indicator
 * that stretches like liquid as it travels between items. Includes a live
 * section read-out, magnetic hover, a periodic light-sweep and a conic ring logo.
 */
const LiquidNav = ({ logo, logoAlt = 'Logo', items = [], activeHref }) => {
  const [isOpen, setIsOpen] = useState(false);
  const listRef = useRef(null);
  const indicatorRef = useRef(null);
  const itemRefs = useRef([]);
  const logoImgRef = useRef(null);
  const reducedRef = useRef(false);
  const hoverIndexRef = useRef(null);

  const safeIndex = items.findIndex((it) => it.href === activeHref);
  const activeIndex = safeIndex === -1 ? 0 : safeIndex;
  const activeLabel = items[activeIndex]?.label ?? '';

  // Move the liquid indicator to a given item index.
  const moveTo = useCallback((index, { stretch = true } = {}) => {
    const el = itemRefs.current[index];
    const indicator = indicatorRef.current;
    const list = listRef.current;
    if (!el || !indicator || !list) return;

    const left = el.offsetLeft;
    const width = el.offsetWidth;

    if (reducedRef.current) {
      gsap.set(indicator, { left, width, scaleX: 1, opacity: 1 });
      return;
    }

    const current = indicator.offsetLeft || left;
    const dist = Math.abs(left - current);
    const stretchAmt = stretch && dist > 6 ? Math.min(1 + dist / 520, 1.4) : 1;

    gsap.to(indicator, { opacity: 1, duration: 0.18, ease: 'power2.out' });
    gsap.to(indicator, {
      left,
      width,
      duration: 0.55,
      ease: 'elastic.out(1, 0.72)',
      overwrite: 'auto',
    });

    if (stretchAmt > 1) {
      gsap.fromTo(
        indicator,
        { scaleX: stretchAmt },
        { scaleX: 1, duration: 0.55, ease: 'elastic.out(1, 0.6)', transformOrigin: '50% 50%' }
      );
    }
  }, []);

  // Initial placement + keep in sync on resize / font load / active change.
  useEffect(() => {
    reducedRef.current = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

    let raf = 0;
    const place = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const target = hoverIndexRef.current ?? activeIndex;
        moveTo(target, { stretch: false });
      });
    };

    place();
    window.addEventListener('resize', place);
    if (document.fonts?.ready) document.fonts.ready.then(place).catch(() => {});

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', place);
    };
  }, [activeIndex, items, moveTo]);

  const handleEnter = (i) => {
    hoverIndexRef.current = i;
    moveTo(i);
  };

  const handleLeaveList = () => {
    hoverIndexRef.current = null;
    moveTo(activeIndex);
  };

  // Subtle magnetic pull on the hovered label.
  const handleItemMove = (e) => {
    if (reducedRef.current) return;
    const label = e.currentTarget.querySelector('.lnav-item__label');
    if (!label) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const mx = e.clientX - rect.left - rect.width / 2;
    gsap.to(label, { x: mx * 0.18, duration: 0.4, ease: 'power3.out' });
  };

  const handleItemOut = (e) => {
    const label = e.currentTarget.querySelector('.lnav-item__label');
    if (label) gsap.to(label, { x: 0, duration: 0.5, ease: 'elastic.out(1, 0.5)' });
  };

  const handleLogoEnter = () => {
    if (!logoImgRef.current || reducedRef.current) return;
    gsap.fromTo(logoImgRef.current, { rotate: 0 }, { rotate: 360, duration: 0.8, ease: 'power3.out' });
  };

  return (
    <div className="lnav-shell">
      <nav className="lnav" aria-label="Primary">
        <a className="lnav-logo" href={items?.[0]?.href || '#hero'} aria-label="Home" onMouseEnter={handleLogoEnter}>
          <span className="lnav-logo__ring" aria-hidden="true" />
          <img src={logo} alt={logoAlt} ref={logoImgRef} />
        </a>

        <div className="lnav-items desktop-only" onMouseLeave={handleLeaveList}>
          <ul className="lnav-list" ref={listRef} role="menubar">
            <span className="lnav-indicator" ref={indicatorRef} aria-hidden="true" />
            {items.map((item, i) => (
              <li key={item.href || `item-${i}`} role="none">
                <a
                  role="menuitem"
                  href={item.href}
                  ref={(el) => {
                    itemRefs.current[i] = el;
                  }}
                  className={`lnav-item${activeIndex === i ? ' is-active' : ''}`}
                  onMouseEnter={() => handleEnter(i)}
                  onMouseMove={handleItemMove}
                  onMouseLeave={handleItemOut}
                >
                  <span className="lnav-item__label">{item.label}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div className="lnav-status desktop-only" aria-hidden="true">
          <span className="lnav-status__dot" />
          <span className="lnav-status__text">{activeLabel}</span>
        </div>

        <button
          className={`lnav-burger mobile-only${isOpen ? ' is-open' : ''}`}
          onClick={() => setIsOpen((v) => !v)}
          aria-label="Toggle menu"
          aria-expanded={isOpen}
        >
          <span />
          <span />
        </button>

        <span className="lnav-sweep" aria-hidden="true" />
      </nav>

      <div className={`lnav-mobile mobile-only${isOpen ? ' is-open' : ''}`}>
        <ul>
          {items.map((item, i) => (
            <li key={item.href || `m-${i}`}>
              <a
                href={item.href}
                className={activeIndex === i ? 'is-active' : ''}
                onClick={() => setIsOpen(false)}
              >
                <span className="lnav-mobile__idx">{String(i + 1).padStart(2, '0')}</span>
                {item.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default LiquidNav;
